import { X } from 'lucide-react';
import { cn } from '@/utils';
import { COOKIE__URL } from '@/constants';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogVisuallyHidden } from '@/components/ui/dialog';
import type { CookieConsentPreferences } from '@/store/slices/cookie-consent.slice';

export const COOKIE_CONSENT_KEY = 'cookie_consent';

export type CookieCategoryState = Omit<CookieConsentPreferences, 'essential'>;

const COOKIE_CATEGORIES: {
	key: keyof CookieCategoryState;
	label: string;
	description: string;
}[] = [
	{
		key: 'preference',
		label: 'Preference Cookies',
		description:
			"Allows the app to keep details such as your preferred language, or region that you're in.",
	},
	{
		key: 'performance',
		label: 'Performance Cookies',
		description:
			'Securely track statistics such as loading speeds and anonymized crash data so our team can squash bugs and improve latency.',
	},
	{
		key: 'marketing',
		label: 'Marketing Cookies',
		description:
			'Tailors your feed to endorse relevant sports events and promos based on your interests.',
	},
];

const CHECKBOX_CLS =
	'h-6 w-6 rounded-[4px] border-2 border-[#848484] data-[state=checked]:bg-blue-custom-2 data-[state=checked]:border-blue-custom-2 data-[state=checked]:text-white';

export const CARD_CLS = cn(
	'bg-white rounded-2xl flex flex-col',
	'shadow-[0px_16px_32px_-8px_rgba(12,12,13,0.4)]',
	"font-['DM_Sans']"
);

export function saveConsent(preferences: CookieConsentPreferences) {
	localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences));
}

export interface CookiePreferencesDialogProps {
	categoryState: CookieCategoryState;
	allSelected: boolean;
	onSelectAll: (checked: boolean) => void;
	onToggle: (key: keyof CookieCategoryState, checked: boolean) => void;
	onRejectAll: () => void;
	onAcceptAndSave: () => void;
	onClose: () => void;
	saveLabel?: string;
}

export function CookiePreferencesDialog({
	categoryState,
	allSelected,
	onSelectAll,
	onToggle,
	onRejectAll,
	onAcceptAndSave,
	onClose,
	saveLabel = 'Accept & Save',
}: CookiePreferencesDialogProps) {
	return (
		<Dialog
			open
			onOpenChange={(isOpen) => {
				if (!isOpen) onClose();
			}}
		>
			<DialogContent
				hideCancelIcon
				aria-describedby={undefined}
				overlayClassName="z-100 bg-black/40 backdrop-blur-none"
				className={cn(
					CARD_CLS,
					'z-101 w-[361px] max-h-[calc(100dvh-32px)] gap-0 overflow-hidden border-0 p-0 sm:rounded-2xl md:w-[calc(100%-32px)] md:max-w-[813px]'
				)}
			>
				<DialogVisuallyHidden title="Your Cookie Preferences" />
				<div className="flex items-center justify-end md:justify-between px-4 pt-3.5 pb-3 shrink-0">
					<p className="hidden md:block flex-1 font-bold text-[16px] leading-[22px] tracking-[-0.6px] text-[#0a0a0a]">
						Your Cookie Preferences
					</p>
					<button
						type="button"
						onClick={onClose}
						className="text-[#2a3e5b] hover:opacity-70 transition-opacity"
					>
						<X size={20} />
					</button>
				</div>

				<div className="flex flex-col gap-8 overflow-y-auto px-4 pb-4">
					<div className="flex flex-col gap-5">
						<p className="md:hidden font-bold text-[16px] leading-[22px] tracking-[-0.6px] text-[#2a3e5b]">
							Your Cookie Preferences
						</p>
						<p className="font-normal text-[14px] leading-[20px] tracking-[-0.392px] text-[#2a3e5b]">
							We use cookies to improve your experience on this website. You may choose which types
							of cookies to allow and change your preferences at any time. Disabling cookies may
							impact your experience on this website. You can learn more by viewing our{' '}
							<a
								href={COOKIE__URL}
								target="_blank"
								rel="noreferrer"
								className="text-[#5583f6] hover:underline"
							>
								Cookie Policy.
							</a>
						</p>
					</div>

					<div className="flex flex-col gap-4">
						<div className="flex gap-2 items-center pb-2 border-b-[0.5px] border-[#dce3f9]">
							<Checkbox
								id="select-all"
								checked={allSelected}
								onCheckedChange={(checked: boolean | 'indeterminate') =>
									onSelectAll(checked === true)
								}
								className={CHECKBOX_CLS}
							/>
							<label
								htmlFor="select-all"
								className="font-medium text-[12px] leading-[14px] tracking-[-0.12px] text-[#0a0a0a] cursor-pointer"
							>
								Select All
							</label>
						</div>

						<div className="flex flex-col gap-4 pl-3">
							<div className="flex flex-col gap-2">
								<div className="flex gap-2 items-center">
									<Checkbox
										id="essential"
										checked
										disabled
										className={cn(CHECKBOX_CLS, 'disabled:opacity-50')}
									/>
									<label
										htmlFor="essential"
										className="font-medium text-[14px] leading-[20px] tracking-[-0.364px] text-[#0a0a0a]"
									>
										Essential Cookies
									</label>
								</div>
								<p className="font-normal text-[14px] leading-[20px] tracking-[-0.392px] text-[#848484]">
									Enables basic functions such as page navigation, secure logins, and realtime
									scoring.
								</p>
							</div>

							{COOKIE_CATEGORIES.map((cat) => (
								<div key={cat.key as string} className="flex flex-col gap-2">
									<div className="flex gap-2 items-center">
										<Checkbox
											id={cat.key as string}
											checked={categoryState[cat.key]}
											onCheckedChange={(checked: boolean | 'indeterminate') =>
												onToggle(cat.key, checked === true)
											}
											className={CHECKBOX_CLS}
										/>
										<label
											htmlFor={cat.key as string}
											className="font-medium text-[14px] leading-[20px] tracking-[-0.364px] text-[#0a0a0a] cursor-pointer"
										>
											{cat.label}
										</label>
									</div>
									<p className="font-normal text-[14px] leading-[20px] tracking-[-0.392px] text-[#848484]">
										{cat.description}
									</p>
								</div>
							))}
						</div>
					</div>

					<div className="flex flex-wrap gap-y-1.5 items-center justify-between">
						<button
							type="button"
							onClick={onRejectAll}
							className="px-5 py-2.5 rounded-full font-medium text-[14px] leading-[20px] tracking-[-0.364px] text-[#5583f6] hover:opacity-80 transition-opacity"
						>
							Reject All
						</button>
						<button
							type="button"
							onClick={onAcceptAndSave}
							className={cn(
								'h-10 min-w-[128px] px-4 rounded-[8px]',
								'bg-blue-custom-2 border-[0.5px] border-[#5583f6]',
								'shadow-[0px_8px_16px_0px_rgba(38,103,255,0.24),0px_2px_6px_0px_rgba(0,0,0,0.15)]',
								'font-medium text-[14px] leading-[20px] tracking-[-0.364px] text-white',
								'hover:bg-[#1a4fd6] transition-colors'
							)}
						>
							{saveLabel}
						</button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
